import React, {useContext, useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import logo from '../../../assets/images/logo.png'
import '../Login.scss'
import './OtpLogin.scss'
import {FormInput} from "../../Form/Input/FormInput";
import {Button} from "../../Form/Button/Button";
import {get} from 'lodash'
import {UserReactContext} from "../../../contexts/UserContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {buttonColors} from "../../Pickers/InputTheme";
import {TypeVerificationCode} from "../TypeVerificationCode/TypeVerificationCode";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {LocalTabs} from "../../../Utils/RoutingTabs/Tabs";
import {isEmailValid} from "../../../Utils/functions";
import {LoginLanguageSelector} from "../LoginLanguageSelector/LoginLanguageSelector";
import {InternationalPhoneInput} from "../../Form/InternationalPhoneInput/InternationalPhoneInput";
import {useCountryCodeCache} from "../../../Utils/hooks/useCountryCodeCache";
import {isValidPhoneNumber} from "react-phone-number-input";
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {WhatsAppIcon} from '../../../Utils/SVGs/WhatsAppIcon';

const defaultDeliveryMethod = 'phone'

const deliveryMethods = {
    phone: 'phone',
    email: 'email',
    whatsapp: 'whatsapp'
}

/**
 * OTP Login component that allows users to login using only a verification code
 * sent to their email or phone number (user's choice)
 *
 * @param {object} props - Component props
 * @param {string} props.token - Login token for direct login
 * @param {string} props.ssoToken - SSO token for SSO login
 * @param {boolean} props.doNotRedirectOnSuccess - Whether to prevent automatic redirect on success
 * @returns {JSX.Element} The OtpLogin component
 */
export const OtpLogin = ({token, ssoToken, doNotRedirectOnSuccess}) => {
    const {i18n, isRtl} = useContext(I18nReactContext);
    const [emailContact, setEmailContact] = useState('');
    // Phone number stored in E.164 format (e.g., '+972501234567') - shared between SMS and WhatsApp tabs
    const [phoneContact, setPhoneContact] = useState('');
    const {countryCode: cachedCountryCode, setCountryCode: setCachedCountryCode} = useCountryCodeCache();
    const [error, setError] = useState('');
    const [mfaStep, setMfaStep] = useState(false);
    const [loading, setLoading] = useState(token || ssoToken ? true : false);
    const [urlLoginCompleted, setUrlLoginCompleted] = useState(false);
    const [companySelectionStep, setCompanySelectionStep] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [pendingOtpRequest, setPendingOtpRequest] = useState(null);
    const [deliveryMethod, setDeliveryMethod] = useState(defaultDeliveryMethod);

    const userContext = useContext(UserReactContext);

    /**
     * Handles changes to the email contact input field
     *
     * @param {object} event - The change event
     */
    const handleEmailContactChange = (event) => {
        setEmailContact(event.target.value)
        setError('') // Clear errors when user types
    }

    /**
     * Handles changes to the phone contact input field
     * Phone value is in E.164 format from react-phone-number-input
     *
     * @param {string} value - Phone number in E.164 format (e.g., '+972501234567')
     */
    const handlePhoneContactChange = (value) => {
        setPhoneContact(value || '')
        setError('') // Clear errors when user types
    }

    /**
     * Handles country change from phone input
     * Caches the selected country for future sessions
     *
     * @param {string} country - Two-letter country code (e.g., 'IL', 'US')
     */
    const handleCountryChange = (country) => {
        if (country) {
            setCachedCountryCode(country)
        }
    }

    /**
     * Handles token login
     */
    const handleTokenLogin = async () => {
        setLoading(true)
        try {
            await userContext.loginWithToken({token})
            setMfaStep({linkLogin: true})
        } catch (e) {
            setError(get(e, 'errors[0].message'))
        }
        finally {
            setLoading(false)
            setUrlLoginCompleted(true)
        }
    }

    /**
     * Handles SSO token login
     */
    const handleSSOLogin = async () => {
        setLoading(true)
        try {
            await userContext.loginWithSSOToken({ssoToken})
        } catch (e) {
            setError(get(e, 'errors[0].message'))
        } finally {
            setLoading(false)
            setUrlLoginCompleted(true)
        }
    }

    /**
     * Handles form submission for OTP login request
     *
     * @param {object} params - The function parameters container object
     * @param {string} params.deliveryMethod - The delivery method ('email' or 'phone')
     * @param {string} params.contact - The contact information (email or phone)
     * @param {string} [params.phoneCountryCode] - The phone country code (required for phone delivery)
     * @param {string} [params.companyId] - The selected company ID
     */
    const handleOtpLogin = async ({deliveryMethod, contact, phoneCountryCode, companyId}) => {
        setLoading(true)
        try {
            setDeliveryMethod(deliveryMethod)
            const response = await userContext.otpLogin({contact, deliveryMethod, phoneCountryCode, companyId})
            
            if (response.requiresCompanySelection) {
                setAvailableCompanies(response.companies)
                setCompanySelectionStep(true)
                setPendingOtpRequest({deliveryMethod, contact, phoneCountryCode})
            } else if (response.mfa) {
                setMfaStep(true)
            }
        } catch (e) {
            setError(get(e, 'errors[0].message'))
        }
        finally {
            setLoading(false)
        }
    }

    /**
     * Handles company selection
     *
     * @param {object} company - The selected company object
     */
    const handleCompanySelection = async (company) => {
        setCompanySelectionStep(false)
        if (pendingOtpRequest) {
            await handleOtpLogin({
                ...pendingOtpRequest,
                companyId: company.id
            })
        }
    }

    useEffect(() => {
        if (token) {
            handleTokenLogin()
        }
        if (ssoToken) {
            handleSSOLogin()
        }
    }, [token, ssoToken])

    /**
     * Renders phone input content for SMS and WhatsApp tabs
     *
     * @param {object} params - The function parameters
     * @param {string} params.deliveryMethod - The delivery method ('phone' or 'whatsapp')
     * @param {string} params.testId - The data-testid for the input
     * @returns {JSX.Element} The phone input content
     */
    const renderPhoneTabContent = ({deliveryMethod, testId}) => (
        <div className="delivery-method-content">
            <InternationalPhoneInput
                value={phoneContact}
                onChange={handlePhoneContactChange}
                onCountryChange={handleCountryChange}
                defaultCountry={cachedCountryCode}
                label={i18n.translate('otpLogin.phoneNumber')}
                placeholder={i18n.translate('otpLogin.phonePlaceholder')}
                required
                error={error}
                dataTestId={testId}
            />
            <div className="login-btn-wrapper">
                <Button
                    color={buttonColors.GREY}
                    type="submit"
                    style={{width: '100%'}}
                    disabled={!phoneContact || !isValidPhoneNumber(phoneContact)}
                    text={i18n.translate('otpLogin.sendVerificationCode')}
                    onClick={() => handleOtpLogin({deliveryMethod, contact: phoneContact, phoneCountryCode: cachedCountryCode})}
                />
            </div>
        </div>
    )

    /**
     * Company Selection Component
     */
    const CompanySelectionStep = () => (
        <div className="company-selection-step">
            <h3 className="company-selection-title">
                {i18n.translate('otpLogin.selectCompany')}
            </h3>
            <p className="company-selection-subtitle">
                {i18n.translate('otpLogin.selectCompanySubtitle')}
            </p>
            <div className="companies-list">
                {availableCompanies.map((company) => (
                    <div 
                        key={company.id} 
                        className="company-option"
                        onClick={() => handleCompanySelection(company)}
                    >
                        <div className="company-name">{company.name}</div>
                    </div>
                ))}
            </div>
            <div className="alternative-login-link">
                <a href="#" onClick={() => setCompanySelectionStep(false)}>
                    {i18n.translate('otpLogin.backToContactInfo')}
                </a>
            </div>
        </div>
    )
    
    // Create tabs configuration for LocalTabs
    const deliveryTabs = [
        {
            value: 'phone',
            label: i18n.translate('otpLogin.sendToPhone'),
            icon: <SmsOutlinedIcon sx={{color: 'black'}} />,
            content: renderPhoneTabContent({deliveryMethod: deliveryMethods.phone, testId: 'otp-phone-input'})
        },
        {
            value: 'whatsapp',
            label: i18n.translate('otpLogin.sendToWhatsApp'),
            icon: <WhatsAppIcon width="24" height="24" color="black" style={{marginInlineEnd: '10px'}} />,
            content: renderPhoneTabContent({deliveryMethod: deliveryMethods.whatsapp, testId: 'otp-whatsapp-input'})
        },
        {
            value: 'email',
            label: i18n.translate('otpLogin.sendToEmail'),
            icon: <MailOutlineIcon sx={{color: 'black'}} />,
            content: (
                <div className="delivery-method-content">
                    <FormInput
                        error={error}
                        dontShowErrorMessage
                        required
                        id="emailContact"
                        label={i18n.translate('otpLogin.emailAddress')}
                        className="login-text-field first"
                        value={emailContact}
                        onChange={handleEmailContactChange}
                        type="email"
                        placeholder="example@company.com"
                    />
                    <div className="login-btn-wrapper">
                        <Button
                            color={buttonColors.GREY}
                            type="submit"
                            style={{width: '100%'}}
                            disabled={!isEmailValid(emailContact)}
                            text={i18n.translate('otpLogin.sendVerificationCode')}
                            onClick={() => handleOtpLogin({deliveryMethod: deliveryMethods.email, contact: emailContact})}
                        />
                    </div>
                </div>
            )
        }
    ];
    // Check if user is already logged in
    if ((!token && !ssoToken) || urlLoginCompleted) {
        const isUserValid = userContext.isUserValid()
        if (!doNotRedirectOnSuccess && isUserValid) {
            return <Navigate to={{pathname: '/home'}}/>
        }
    }

    return (loading ? <LoadingSpinner/> : <div className={`login-container ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
        <LoginLanguageSelector position="top-right" />
        <img className="loginLogo" src={logo} alt="WeezBee Logo"/>
        <div className="login-title">
            <h2>{i18n.translate('otpLogin.title')}</h2>
        </div>
        {!mfaStep && !companySelectionStep ? <div className="login-form">
                <div className="otp-login-tabs">
                    <LocalTabs tabs={deliveryTabs} defaultTab={deliveryMethod} variant="fullWidth" scrollButtons={false} onTabChange={setDeliveryMethod} />
                </div>
            </div> :
            mfaStep ? <div className="login-form">
                <TypeVerificationCode linkLogin={get(mfaStep, 'linkLogin')} deliveryMethod={deliveryMethod}/>
            </div> :
            <div className="login-form">
                <CompanySelectionStep />
            </div>
        }
    </div>)
} 